/**
 * Character looks: the catalogue of interchangeable crewmate parts and colours,
 * and the compact code the server stores per player (`PlayerState.look`).
 * Every choice is a part of `crewmate_custom.glb` (tools/models/crewmate_custom.py
 * builds exactly these ids). An empty code means the classic crewmate.
 */
export const LOOK_CATEGORIES = {
  race: { label: "Race", options: { human: "Human", merfolk: "Merfolk", skeleton: "Cursed skeleton" } },
  eyes: { label: "Eyes", options: { both: "Both eyes", patchRight: "Eyepatch · right", patchLeft: "Eyepatch · left", spectacles: "Spectacles", scar: "Scarred shut" } },
  hair: { label: "Hair", options: { bald: "Bald", short: "Short", long: "Long with queue", wild: "Wild tufts" } },
  beard: { label: "Facial hair", options: { none: "Clean-shaven", goatee: "Moustache & goatee", full: "Braided beard", walrus: "Walrus & chops" } },
  headProp: { label: "Head prop", options: { none: "Nothing", tricorn: "Tricorn", plumed: "Plumed tricorn", bicorne: "Bicorne", bandana: "Bandana", cap: "Watch cap" } },
  clothing: { label: "Clothing", options: { frock: "Frock coat", vest: "Vest & rolled sleeves", greatcoat: "Greatcoat", peacoat: "Pea jacket & Breton" } },
  boots: { label: "Boots", options: { cavalier: "Cavalier boots", buckled: "Buckled shoes", work: "Work boots", wraps: "Ankle wraps" } },
} as const;

export type LookCategory = keyof typeof LOOK_CATEGORIES;
export type LookChoice<C extends LookCategory> = keyof (typeof LOOK_CATEGORIES)[C]["options"] & string;
export const LOOK_CATEGORY_IDS = Object.keys(LOOK_CATEGORIES) as LookCategory[];

/** Tint channels: each swatch list is the full set a look may use (codes store indices). */
export const LOOK_COLORS = {
  skin: { label: "Skin", swatches: ["#f3d2b8", "#e8b892", "#d39a6e", "#b57a4f", "#8a5a3c", "#5e3a26", "#3e2618", "#4fa396", "#3f7f95", "#6f9a4f", "#e8dfc8", "#cfc6b0", "#b8c6c8"] },
  hairColor: { label: "Hair", swatches: ["#16100c", "#3a2618", "#6b4428", "#a5652e", "#c9a25a", "#e3ddd2", "#8f8a84", "#9c2f22", "#2e6b5e"] },
  eyeColor: { label: "Eyes", swatches: ["#3d6e9c", "#5a3b1e", "#4f7a4a", "#7a8790", "#b08a2e", "#7a3fa0", "#c23b2f"] },
} as const;

export type LookColor = keyof typeof LOOK_COLORS;
export const LOOK_COLOR_IDS = Object.keys(LOOK_COLORS) as LookColor[];

export type CharacterLook = { [C in LookCategory]: LookChoice<C> } & { [K in LookColor]: string };

/** Each race's natural skin, applied when the race changes in the creator. */
export const RACE_SKIN: Readonly<Record<LookChoice<"race">, string>> = { human: "#e8b892", merfolk: "#4fa396", skeleton: "#e8dfc8" };

export const DEFAULT_LOOK: Readonly<CharacterLook> = {
  race: "human", eyes: "both", hair: "short", beard: "goatee", headProp: "tricorn", clothing: "frock", boots: "cavalier",
  skin: "#e8b892", hairColor: "#3a2618", eyeColor: "#3d6e9c",
};

const CODE_VERSION = "1";
export const LOOK_CODE_LENGTH = 1 + LOOK_CATEGORY_IDS.length + LOOK_COLOR_IDS.length;

function choices<C extends LookCategory>(category: C): LookChoice<C>[] {
  return Object.keys(LOOK_CATEGORIES[category].options) as LookChoice<C>[];
}

/** Compact, URL-safe code: version, then one base-36 index per category and colour. */
export function encodeLook(look: CharacterLook): string {
  return CODE_VERSION
    + LOOK_CATEGORY_IDS.map((c) => choices(c).indexOf(look[c] as never).toString(36)).join("")
    + LOOK_COLOR_IDS.map((k) => (LOOK_COLORS[k].swatches as readonly string[]).indexOf(look[k]).toString(36)).join("");
}

/** Null for anything that is not a valid current-version code. */
export function decodeLook(code: string): CharacterLook | null {
  if (code.length !== LOOK_CODE_LENGTH || code[0] !== CODE_VERSION || !/^[0-9a-z]+$/.test(code)) return null;
  const look: Record<string, string> = {};
  let i = 1;
  for (const category of LOOK_CATEGORY_IDS) {
    const choice = choices(category)[parseInt(code[i++]!, 36)];
    if (choice === undefined) return null;
    look[category] = choice;
  }
  for (const key of LOOK_COLOR_IDS) {
    const swatch = LOOK_COLORS[key].swatches[parseInt(code[i++]!, 36)];
    if (swatch === undefined) return null;
    look[key] = swatch;
  }
  return look as CharacterLook;
}

/** Untrusted look payload: "" (classic crewmate) or a valid code; null otherwise. */
export function parseLook(raw: unknown): string | null {
  if (raw === "") return "";
  return typeof raw === "string" && decodeLook(raw) ? raw : null;
}

/** Deterministic look from any seed: the race picks a fitting skin. */
export function randomLook(seed: string): CharacterLook {
  let state = 2166136261;
  for (let i = 0; i < seed.length; i++) state = Math.imul(state ^ seed.charCodeAt(i), 16777619);
  const next = (n: number): number => {
    state = Math.imul(state ^ (state >>> 15), 2246822507) ^ Math.imul(state ^ (state >>> 13), 3266489909);
    return ((state ^= state >>> 16) >>> 0) % n;
  };
  const look: Record<string, string> = {};
  for (const category of LOOK_CATEGORY_IDS) {
    const options = choices(category);
    look[category] = options[next(options.length)]!;
  }
  for (const key of LOOK_COLOR_IDS) look[key] = LOOK_COLORS[key].swatches[next(LOOK_COLORS[key].swatches.length)]!;
  const race = look.race as LookChoice<"race">;
  const skins = LOOK_COLORS.skin.swatches;
  // Humans draw from the human tones, other races from their own.
  look.skin = race === "human" ? skins[next(7)]! : race === "merfolk" ? skins[7 + next(3)]! : skins[10 + next(3)]!;
  return look as CharacterLook;
}
